import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronLeft, Send, Star, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { useBackTarget } from '../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = 'var(--accent-purple)';

const CATEGORIES = [
  { id: 'idea', label: 'Idea', emoji: '💡' },
  { id: 'bug', label: 'Bug', emoji: '🐞' },
  { id: 'praise', label: 'Praise', emoji: '✨' },
  { id: 'other', label: 'Other', emoji: '✍️' },
];

export default function SupportFeedbackPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const token = useMemo(() => localStorage.getItem('token'), []);

  const [category, setCategory] = useState(CATEGORIES[0].id);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (message.trim().length < 10) {
      toast.error('Please share a bit more (at least 10 characters)');
      return;
    }
    setSubmitting(true);
    try {
      const cat = CATEGORIES.find((c) => c.id === category)?.label || category;
      const body =
        `Category: ${cat}\n` +
        (rating ? `Rating: ${rating}/5\n` : '') +
        `\n${message.trim()}`;
      await axios.post(
        `${API}/support/requests`,
        { reason: 'Feedback', message: body },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubmitted(true);
      toast.success('Thanks — your feedback was sent to the team');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="app-shell"
        data-testid="support-feedback-success"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--app-bg)',
          padding: 24,
        }}
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.35 }}
          style={{ textAlign: 'center', maxWidth: 320 }}
        >
          <CheckCircle2 size={62} color={BRAND} strokeWidth={1.7} />
          <h1
            style={{
              fontFamily: FONT,
              fontSize: 24,
              color: 'var(--text)',
              margin: '14px 0 6px',
            }}
          >
            Thank you!
          </h1>
          <p
            style={{
              fontFamily: SUB_FONT,
              color: 'var(--text-2)',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            Your feedback shapes Allure. The team reads every message and replies when relevant.
          </p>
          <button
            data-testid="support-feedback-done-button"
            onClick={() => navigate('/support')}
            style={{
              marginTop: 22,
              padding: '12px 22px',
              border: 'none',
              borderRadius: 999,
              background: 'var(--accent-purple)',
              color: '#FFFFFF',
              fontFamily: FONT,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 12px 24px rgba(196, 18, 48, 0.16)',
            }}
          >
            Back to Support
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      data-testid="support-feedback-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        backgroundImage:
          'radial-gradient(ellipse 600px 360px at 50% -140px, rgba(196, 18, 48, 0.10) 0%, transparent 60%)',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 390,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '14px 0 0',
        }}
      >
        <button
          onClick={goBack}
          data-testid="support-feedback-back-button"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 999,
            cursor: 'pointer',
            padding: 6,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'var(--premium-shadow-nested)',
          }}
        >
          <ChevronLeft size={22} color="var(--text)" strokeWidth={2} />
        </button>
        <NotificationBellButton
          buttonTestId="support-feedback-bell-button"
          popupTestId="support-feedback-notification-popup"
        />
      </div>

      <RouteTransitionLogo width={80} height={72} style={{ marginTop: 18 }} />
      <h1
        style={{
          fontFamily: FONT,
          fontSize: 24,
          fontWeight: 400,
          color: 'var(--text)',
          margin: '8px 0 2px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        Share your feedback
        <Sparkles size={20} color={BRAND} strokeWidth={2} />
      </h1>
      <p
        style={{
          margin: 0,
          fontFamily: SUB_FONT,
          fontSize: 13,
          color: 'var(--text-2)',
          textAlign: 'center',
          maxWidth: 280,
          lineHeight: 1.5,
        }}
      >
        Ideas, bugs, kudos — the team reads every single one.
      </p>

      <div
        style={{
          width: '100%',
          maxWidth: 360,
          marginTop: 18,
          background: 'var(--surface)',
          borderRadius: 22,
          border: '1px solid rgba(196, 18, 48, 0.06)',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
          padding: '16px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Category */}
        <div>
          <label
            style={{
              display: 'block',
              fontFamily: SUB_FONT,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-2)',
              marginBottom: 8,
            }}
          >
            What kind of feedback?
          </label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
            }}
          >
            {CATEGORIES.map((c) => {
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`support-feedback-category-${c.id}`}
                  onClick={() => setCategory(c.id)}
                  style={{
                    padding: '10px 6px',
                    background: active ? BRAND : 'var(--surface-2)',
                    color: active ? '#FFFFFF' : 'var(--text)',
                    border: active ? `1px solid ${BRAND}` : '1px solid rgba(196, 18, 48, 0.08)',
                    borderRadius: 12,
                    fontFamily: SUB_FONT,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    transition: 'all 180ms ease',
                    boxShadow: active ? 'var(--premium-shadow-main)' : 'none',
                  }}
                >
                  <span style={{ fontSize: 18 }} aria-hidden="true">{c.emoji}</span>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rating */}
        <div>
          <label
            style={{
              display: 'block',
              fontFamily: SUB_FONT,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-2)',
              marginBottom: 8,
            }}
          >
            Rate Allure (optional)
          </label>
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px 0',
            }}
          >
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = (hoverRating || rating) >= n;
              return (
                <button
                  key={n}
                  data-testid={`support-feedback-star-${n}`}
                  onClick={() => setRating(n === rating ? 0 : n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    transition: 'transform 160ms ease',
                    transform: filled ? 'scale(1.05)' : 'scale(1)',
                  }}
                >
                  <Star
                    size={30}
                    strokeWidth={1.6}
                    color={filled ? '#F5B81C' : '#D5CFE8'}
                    fill={filled ? '#F5B81C' : 'transparent'}
                  />
                </button>
              );
            })}
          </div>
        </div>

        {/* Message */}
        <div>
          <label
            style={{
              display: 'block',
              fontFamily: SUB_FONT,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-2)',
              marginBottom: 8,
            }}
          >
            Your message
          </label>
          <textarea
            data-testid="support-feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            placeholder="What would make Allure better for you?"
            style={{
              width: '100%',
              border: '1px solid var(--card-border)',
              borderRadius: 14,
              padding: '12px 14px',
              fontFamily: SUB_FONT,
              fontSize: 14,
              resize: 'vertical',
              minHeight: 120,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <button
          data-testid="support-feedback-submit"
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '12px 14px',
            border: 'none',
            borderRadius: 14,
            background: submitting
              ? 'var(--accent-surface)'
              : 'var(--accent-purple)',
            color: '#FFFFFF',
            fontFamily: FONT,
            fontSize: 16,
            fontWeight: 700,
            cursor: submitting ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            boxShadow: 'var(--premium-shadow-main)',
          }}
        >
          <Send size={15} strokeWidth={2.2} />
          {submitting ? 'Sending…' : 'Send Feedback'}
        </button>
      </div>
    </div>
  );
}
